import { apiGetCustomerApplication, apiGetCustomerApplicationTemplate, apiUpdateCustomerApplication } from "Hooks/Api/apiCustomerApplications";
import useAsync from "Hooks/useAsync";
import { observer } from "mobx-react";
import { ReactNode } from "react";
import { RouteComponentProps } from "react-router";
import { PageBreadcrumbs } from "Views/Components/Breadcrumbs/PageBreadcrumbs";
import CustomerApplicationFormRenderer from "Views/Components/CustomerApplications/CustomerApplicationFormRenderer";
import AccessIntelSecuredPage from "Views/Components/Security/AccessIntelSecuredPage";
import InlineSpinner from "Views/Components/Spinner/InlineSpinner";
import { convertStringToTemplateForm, TemplateFormProvider } from "Views/Components/TemplateForm/TemplateFormContext";
import { FormFieldDefinition } from "Views/Components/TemplateForm/TemplateFormTypes";
import { useCustomerApplicationAuthorisation } from "./useCustomerApplicationAuthorisation";
import { store } from "Models/Store";

const Tags = [
	{ label: 'Customer Applications', link: '/approve/customerapplications' },
	{ label: 'Edit Applicant' },
];

export interface EditCustomerApplicationPageProps { id: string }


const EditCustomerApplicationPage = observer((props: RouteComponentProps<EditCustomerApplicationPageProps>) => {
	const { match } = props;
	const { id } = match.params;

	const { data, type } = useAsync(async () => {

		const applicationData = (await apiGetCustomerApplication(id)).data;
		let templateSchemaString = "[]";
		if (applicationData.customerApplicationTemplateId) {
			const response = await apiGetCustomerApplicationTemplate(applicationData.customerApplicationTemplateId);
			templateSchemaString = response.data.jsonSchema ?? "[]";
		}

		const templateSchema = JSON.parse(templateSchemaString) as FormFieldDefinition[];
		const formData = convertStringToTemplateForm(applicationData.jsonSchemaSubmission ?? {}, templateSchema ?? []);

		return {
			applicationData,
			templateSchema,
			formData
		}

	}, [id]);

	const {
		canUserEditApplication
	} = useCustomerApplicationAuthorisation(store.getUser);

	// Loading view
	if (type === 'loading' || type === 'error' || !data.applicationData || !data.templateSchema) {
		return (
			<EditCustomerApplicationPageContentWrapper {...props}>
				<InlineSpinner />
			</EditCustomerApplicationPageContentWrapper>
		)
	}

	// Readonly Scenarios
	// 1) Application has been actioned
	const hasReadonlyStatus = ['PENDING', 'APPROVED', 'REFERRED', 'DECLINED'].includes(data?.applicationData?.status ?? "");;
	// 2) Application is being viewed by a userType that can't edit an application they can see.
	const hasEditApplicationPermission  = canUserEditApplication(data.applicationData);
	// a) User / Restricted User can see applications under templates they can see, but  cannot edit them.
	const isReadOnly = hasReadonlyStatus || !hasEditApplicationPermission;

	// Content View
	return (
		<EditCustomerApplicationPageContentWrapper {...props}>
			<TemplateFormProvider
				initialFormData={data.formData}
				initSchema={data.templateSchema}
				isReadonly={isReadOnly}
			>
				<CustomerApplicationFormRenderer
					initialModelData={data.applicationData}
					onSubmitRequest={apiUpdateCustomerApplication}
				/>
			</TemplateFormProvider>
		</EditCustomerApplicationPageContentWrapper>
	)
})

export default EditCustomerApplicationPage;


function EditCustomerApplicationPageContentWrapper(props: RouteComponentProps<EditCustomerApplicationPageProps> & {
	children: ReactNode
}) {
	const { children } = props;

	return (
		<AccessIntelSecuredPage routeComponentProps={props} product="approve">
			<div className="body-content customer-application-page">
				<div className="invisible-page-wrap">
					<div className="top-container">
						<PageBreadcrumbs tags={Tags} />
					</div>
					<div className="white-box">
						{children}
					</div>
				</div>
			</div>
		</AccessIntelSecuredPage>
	)
}